import {
  faChevronDown,
  faChevronRight,
  faFrown,
  faShield,
  faSpinner
} from '@fortawesome/pro-regular-svg-icons'
import { useState } from 'react'
import Button from 'components/Button'
import {
  CollectiveProposalQueryResult,
  useProposalChangedSubscription,
  VotingAnonymity
} from 'graphql/generated'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useListProposalVotes } from 'hooks/collectives/useListProposalVotes'
import { Typography } from 'components/common'
import SurveyVoteListItem from './SurveyVoteListItem'
import { useBreakpoint } from 'hooks/useBreakpoint'

interface IProps {
  proposal: NonNullable<CollectiveProposalQueryResult['collectiveProposal']>
}
const headerContainerClasses = 'flex flex-row justify-between cursor-pointer'
const noItemsContainerClasses = 'flex flex-row justify-center items-center'
const noItemsIconClasses = 'mr-[10px] text-[15px] text-gray-500'
const noItemsTextClasses = 'text-[15px]'
const headerCellClasses =
  'py-[10px] px-[20px] font-sans text-gray-500 uppercase text-center text-[12px] font-medium'

const SurveyVotesTable: React.FC<IProps> = ({ proposal }) => {
  const [show, setShow] = useState(false)
  const { edges, pageInfo, loadMore, loadingMore, refetch } =
    useListProposalVotes(proposal.id)

  useProposalChangedSubscription({
    variables: {
      proposalId: proposal.id
    },
    onData: () => refetch()
  })

  const votes = edges?.map(x => x.node) || []

  const { isSm } = useBreakpoint()

  if (!votes) {
    return null
  }

  if (!votes.length) {
    return (
      <div className="bg-gray-50 p-[15px] rounded-xl">
        <div className={headerContainerClasses} onClick={() => setShow(!show)}>
          <Typography fontWeight="light" color="gray" className={'pr-[7px]'}>
            {`Poll Answers`}
          </Typography>
        </div>

        <div className={noItemsContainerClasses}>
          <FontAwesomeIcon
            icon={faFrown}
            size={'lg'}
            className={noItemsIconClasses}
          />
          <Typography color="gray" className={noItemsTextClasses}>
            {'There are no answers for this poll yet'}
          </Typography>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gray-50 p-[15px] rounded-xl">
      <div className={headerContainerClasses} onClick={() => setShow(!show)}>
        <Typography fontWeight="light" color="gray" className={'pr-[7px]'}>
          {`Poll Respondents`}
        </Typography>

        <div className="flex justify-center items-center w-[30px] rounded-xl">
          <FontAwesomeIcon icon={show ? faChevronDown : faChevronRight} />
        </div>
      </div>

      {show && (
        <div className={'mt-5'}>
          <div className={'border border-gray-200 overflow-hidden rounded-xl'}>
            <table className="divide-y divide-gray-200 w-full">
              {isSm && (
                <thead className={'bg-white'}>
                  <tr>
                    <th className={headerCellClasses}>Respondent</th>
                    {proposal.votingAnonymity ===
                      VotingAnonymity.TRANSPARENT && (
                      <th className={headerCellClasses}>Answer</th>
                    )}
                    {proposal.requireVotingSignature && (
                      <th className={headerCellClasses}>Signature</th>
                    )}
                  </tr>
                </thead>
              )}

              <tbody className="bg-white divide-y divide-gray-200">
                {votes.map(vote => (
                  <SurveyVoteListItem
                    key={`vote-${vote.id}`}
                    proposal={proposal}
                    vote={vote}
                    showVote={
                      proposal.votingAnonymity === VotingAnonymity.TRANSPARENT
                    }
                    showSignature={proposal.requireVotingSignature}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {pageInfo?.hasNextPage && (
            <div className="flex flex-row justify-center items-center py-2">
              <Button
                label={loadingMore ? 'Loading...' : 'See More'}
                color={'white'}
                onClick={() => loadMore()}
                disabled={loadingMore}
              />
            </div>
          )}

          {loadingMore && (
            <div className={'py-[10px] flex justify-center'}>
              <FontAwesomeIcon icon={faSpinner} spin size={'2x'} />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default function ProposalVotes({ proposal }: IProps) {
  if (proposal.votingAnonymity === VotingAnonymity.ANONYMOUS) {
    return (
      <div className="bg-gray-50 p-[15px] rounded-xl">
        <div className={headerContainerClasses}>
          <Typography fontWeight="light" color="gray" className={'pr-[7px]'}>
            {`Poll Answers`}
          </Typography>
        </div>

        <div className={noItemsContainerClasses}>
          <FontAwesomeIcon
            icon={faShield}
            size={'lg'}
            className={noItemsIconClasses}
          />
          <Typography color="gray" className={noItemsTextClasses}>
            {`This poll's answers are anonymous`}
          </Typography>
        </div>
      </div>
    )
  }

  return <SurveyVotesTable proposal={proposal} />
}
